<script setup>
import { useForm, Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import DriverPortalLayout from '@/Layouts/DriverPortalLayout.vue'
import { CHANNEL_CONFIG } from '@/composables/useChannelConfig.js'

defineOptions({ layout: DriverPortalLayout })

const { t } = useI18n()

const props = defineProps({
  driver:  { type: Object, required: true },
  account: { type: Object, required: true }
})

const form = useForm({
  current_password:      '',
  password:              '',
  password_confirmation: ''
})

function channelLabel(platform) {
  return CHANNEL_CONFIG[platform]?.label || platform
}

function channelColor(platform) {
  return CHANNEL_CONFIG[platform]?.color || null
}

function submit() {
  form.transform(data => ({ portal_account: data }))
    .patch('/profile/password', {
      onSuccess: () => form.reset()
    })
}

function formatDatetime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

function licenseStatusColor(status) {
  if (status === 'ok') return 'var(--ok)'
  if (status === 'expiring_soon') return 'var(--warn)'
  if (status === 'expired') return 'var(--danger)'
  return 'var(--muted)'
}
</script>

<template>
  <h1 class="page-title">{{ t('driver_portal.profile.title') }}</h1>

  <div class="layout">

    <!-- Card 1: Your details (read-only) -->
    <section class="card">
      <h2 class="card-title">{{ t('driver_portal.profile.sections.details') }}</h2>

      <div v-if="driver.photo_url" class="photo-wrap">
        <img :src="driver.photo_url" :alt="driver.full_name" class="driver-photo" />
      </div>

      <dl class="detail-list">
        <div class="detail-row">
          <dt>{{ t('driver_portal.profile.fields.name') }}</dt>
          <dd>{{ driver.full_name }}</dd>
        </div>
        <div v-if="driver.phone" class="detail-row">
          <dt>{{ t('driver_portal.profile.fields.phone') }}</dt>
          <dd>{{ driver.phone }}</dd>
        </div>
        <div v-if="driver.license_number" class="detail-row">
          <dt>{{ t('driver_portal.profile.fields.license_number') }}</dt>
          <dd>{{ driver.license_number }}</dd>
        </div>
        <div v-if="driver.license_expiry" class="detail-row">
          <dt>{{ t('driver_portal.profile.fields.license_expiry') }}</dt>
          <dd>
            {{ driver.license_expiry }}
            <span
              v-if="driver.license_status !== 'unknown'"
              class="status-pill"
              :style="{ color: licenseStatusColor(driver.license_status), background: `color-mix(in oklch, ${licenseStatusColor(driver.license_status)} 12%, var(--surface))` }"
            >
              {{ t(`driver_portal.profile.license_status.${driver.license_status}`) }}
            </span>
          </dd>
        </div>
        <div v-if="driver.channels && driver.channels.length > 0" class="detail-row detail-row--channels">
          <dt>{{ t('driver_portal.profile.sections.channels') }}</dt>
          <dd>
            <ul class="channel-list">
              <li v-for="ch in driver.channels" :key="ch.platform + ch.handle" class="channel-item">
                <span
                  class="channel-chip"
                  :style="channelColor(ch.platform) ? { background: `color-mix(in oklch, ${channelColor(ch.platform)} 15%, var(--surface))`, color: channelColor(ch.platform) } : {}"
                >
                  {{ channelLabel(ch.platform) }}
                </span>
                <span class="channel-handle">{{ ch.handle }}</span>
              </li>
            </ul>
          </dd>
        </div>
      </dl>
      <p class="hint">{{ t('driver_portal.profile.hint') }}</p>
    </section>

    <!-- Card 2: Change password -->
    <section class="card">
      <h2 class="card-title">{{ t('driver_portal.profile.sections.password') }}</h2>
      <form id="password-form" @submit.prevent="submit" novalidate>
        <div class="field">
          <label for="current_password" class="label">{{ t('driver_portal.profile.change.current') }}</label>
          <input
            id="current_password"
            v-model="form.current_password"
            type="password"
            class="input"
            autocomplete="current-password"
            required
          />
          <p v-if="form.errors.current_password" class="field-error">{{ form.errors.current_password }}</p>
        </div>

        <div class="field">
          <label for="new_password" class="label">{{ t('driver_portal.profile.change.new') }}</label>
          <input
            id="new_password"
            v-model="form.password"
            type="password"
            class="input"
            autocomplete="new-password"
            required
          />
          <p v-if="form.errors.password" class="field-error">{{ form.errors.password }}</p>
        </div>

        <div class="field">
          <label for="password_confirmation" class="label">{{ t('driver_portal.profile.change.confirm') }}</label>
          <input
            id="password_confirmation"
            v-model="form.password_confirmation"
            type="password"
            class="input"
            autocomplete="new-password"
            required
          />
          <p v-if="form.errors.password_confirmation" class="field-error">{{ form.errors.password_confirmation }}</p>
        </div>

        <button type="submit" class="btn-primary" :disabled="form.processing">
          {{ form.processing ? t('driver_portal.profile.change.cta.loading') : t('driver_portal.profile.change.cta.idle') }}
        </button>
      </form>
    </section>

    <!-- Card 3: Account info -->
    <section class="card">
      <h2 class="card-title">{{ t('driver_portal.profile.sections.account') }}</h2>
      <dl class="detail-list">
        <div class="detail-row">
          <dt>{{ t('driver_portal.profile.fields.sign_in_email') }}</dt>
          <dd>{{ account.email }}</dd>
        </div>
        <div class="detail-row">
          <dt>{{ t('driver_portal.profile.fields.total_sign_ins') }}</dt>
          <dd>{{ account.sign_in_count }}</dd>
        </div>
        <div class="detail-row">
          <dt>{{ t('driver_portal.profile.fields.last_sign_in') }}</dt>
          <dd>{{ formatDatetime(account.last_sign_in_at) }}</dd>
        </div>
        <div class="detail-row">
          <dt>{{ t('driver_portal.profile.fields.current_session') }}</dt>
          <dd>{{ formatDatetime(account.current_sign_in_at) }}</dd>
        </div>
      </dl>
    </section>

  </div>

  <div class="back-row">
    <Link href="/" class="back-link">← {{ t('driver_portal.profile.back') }}</Link>
  </div>
</template>

<style scoped>
.page-title {
  font-family: var(--font-display, var(--display));
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 1.5rem;
}

.layout {
  display: grid;
  gap: 1.25rem;
}

@media (min-width: 768px) {
  .layout {
    grid-template-columns: 1fr 1fr;
  }
}

.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 1rem;
  padding-bottom: 0.625rem;
  border-bottom: 1px solid var(--line);
}

.photo-wrap {
  margin-bottom: 1rem;
}

.driver-photo {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--line);
}

.detail-list {
  display: grid;
  gap: 0.625rem;
  margin: 0;
}

.detail-row {
  display: grid;
  grid-template-columns: 10rem 1fr;
  gap: 0.5rem;
  align-items: baseline;
}

.detail-row--channels {
  align-items: start;
}

.detail-row dt {
  font-size: 0.8125rem;
  color: var(--muted);
}

.detail-row dd {
  font-size: 0.9375rem;
  color: var(--ink);
  margin: 0;
  word-break: break-word;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.status-pill {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  white-space: nowrap;
}

.channel-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.channel-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.channel-chip {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: color-mix(in oklch, var(--accent) 12%, var(--surface));
  color: var(--accent);
  white-space: nowrap;
}

.channel-handle {
  font-size: 0.875rem;
  color: var(--inkSoft);
}

.hint {
  margin: 1rem 0 0;
  font-size: 0.8125rem;
  color: var(--muted);
  line-height: 1.5;
}

/* Change password form */
.field {
  margin-bottom: 1rem;
}

.label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--inkSoft);
  margin-bottom: 0.375rem;
}

.input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: var(--radius-input, 8px);
  color: var(--ink);
  font-size: 0.9375rem;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.input:focus {
  border-color: var(--accent);
  outline: 2px solid color-mix(in oklch, var(--accent) 30%, transparent);
  outline-offset: 1px;
}

.field-error {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: var(--danger);
}

.btn-primary {
  margin-top: 0.25rem;
  padding: 0.5rem 1.25rem;
  background: var(--accent);
  color: var(--accentInk);
  border: none;
  border-radius: var(--radius-button, 8px);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Back row */
.back-row {
  margin-top: 1.5rem;
}

.back-link {
  font-size: 0.9375rem;
  color: var(--accent);
  text-decoration: none;
}

.back-link:hover {
  text-decoration: underline;
}
</style>
